import { motion } from "framer-motion";
import PageTransition from "../components/PageTransition";
import PageHero from "../components/PageHero";
import ImagePlaceholder from "../components/ImagePlaceholder";
import { useTranslation } from "../i18n/LanguageContext";

const fadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

export default function About() {
  const t = useTranslation();

  const valeurs = [
    { title: t.val_quality, description: t.val_quality_desc },
    { title: t.val_safety, description: t.val_safety_desc },
    { title: t.val_innovation, description: t.val_innovation_desc },
    { title: t.val_deadlines, description: t.val_deadlines_desc },
    { title: t.val_environment, description: t.val_environment_desc },
    { title: t.val_satisfaction, description: t.val_satisfaction_desc },
  ];

  return (
    <PageTransition>
      <PageHero title={t.about_title} description={t.about_subtitle} />

      <section className="py-24 md:py-32">
        <div className="max-w-content mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-5 gap-20">
            <div className="lg:col-span-3">
              <div className="md:columns-2 md:gap-12 space-y-6 text-[17px] leading-[1.8] text-secondary">
                <p>{t.about_p1}</p>
                <p>{t.about_p2}</p>
                <p>{t.about_p3}</p>
                <p>{t.about_p4}</p>
                <p>{t.about_p5}</p>
              </div>

              <div className="mt-14 pt-10 border-t border-border">
                <h3 className="text-[12px] uppercase tracking-[0.1em] font-semibold text-primary mb-5">{t.about_legal}</h3>
                <dl className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-[15px]">
                  <div>
                    <dt className="text-secondary mb-1">RCCM</dt>
                    <dd className="font-medium text-primary">CD/LSH/RCCM/24-B-01387</dd>
                  </div>
                  <div>
                    <dt className="text-secondary mb-1">NIF</dt>
                    <dd className="font-medium text-primary">A2426094Q</dd>
                  </div>
                  <div>
                    <dt className="text-secondary mb-1">ID. NAT</dt>
                    <dd className="font-medium text-primary">05-F4200-N68156T</dd>
                  </div>
                </dl>
              </div>
            </div>

            <div className="lg:col-span-2">
              <ImagePlaceholder
                label="RAVA"
                src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=680&q=80&auto=format&fit=crop"
                aspectRatio="3/4"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-subtle border-t border-border">
        <div className="max-w-content mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeIn} transition={{ duration: 0.4 }}>
            <p className="text-[12px] uppercase tracking-[0.15em] font-semibold text-accent mb-4">{t.about_values_label}</p>
            <h2 className="text-heading-sm md:text-heading font-serif mb-16">{t.about_values_title}</h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {valeurs.map((v, i) => (
              <motion.div
                key={v.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="border border-border bg-white p-10"
              >
                <h3 className="font-serif text-xl mb-3">{v.title}</h3>
                <p className="text-secondary text-[15px] leading-relaxed">{v.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 border-t border-border">
        <div className="max-w-content mx-auto px-6 md:px-12">
          <p className="text-[12px] uppercase tracking-[0.15em] font-semibold text-accent mb-4">{t.about_partners_label}</p>
          <h2 className="text-heading-sm md:text-heading font-serif mb-6">{t.about_partners_title}</h2>
          <p className="text-secondary text-lg max-w-2xl mb-14">{t.about_partners_text}</p>

          <div className="grid sm:grid-cols-2 gap-6 max-w-xl">
            <div className="border border-border p-8">
              <h3 className="font-serif text-lg mb-1">Université McGill</h3>
              <p className="text-secondary text-[15px]">Montréal, Canada</p>
            </div>
            <div className="border border-border p-8">
              <h3 className="font-serif text-lg mb-1">Omics Data Solutions Inc.</h3>
              <p className="text-secondary text-[15px]">Data Science & IA</p>
            </div>
          </div>
        </div>
      </section>
    </PageTransition>
  );
}
